import React, { useState, useRef, useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  Shield, CheckCircle, Lock, Eye, EyeOff, Mail, KeyRound, ArrowLeft, Loader2,
  Zap, Sparkles,
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import axios from 'axios';

const API = '/api';

// Login + Register — Swiss Minimalist + Pastel theme.
// Pastels: sage #D4E0DA (sign-in) · peach #EBD8C8 (register) · lavender #D9CFD9 (accent)
// Fonts: .font-display (Playfair) · .font-body (Manrope) · .font-label (Outfit)

// Editorial styling for shadcn Input fields
const inputCx =
  'bg-[#FAFAFA] dark:bg-[#050505] border-black/15 dark:border-white/15 focus-visible:border-[#0A0A0A] dark:focus-visible:border-white/30 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-xl h-12 text-sm placeholder:text-black/35 dark:placeholder:text-white/40';

const Login = ({ initialMode = 'login' }) => {
  const { user, currentWorkspace, workspaces, login, register, setUser, setWorkspaces, setCurrentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  // Login mode: 'otp' or 'password'
  const [loginMode, setLoginMode] = useState('otp');
  const [isLogin, setIsLogin] = useState(initialMode !== 'register');
  const [showPassword, setShowPassword] = useState(false);

  // OTP states
  const [otpStep, setOtpStep] = useState('email'); // 'email' or 'verify'
  const [otpEmail, setOtpEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const otpInputRefs = useRef([]);

  // Password login states
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    workspaceName: ''
  });
  const [loading, setLoading] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  // Sync isLogin with route changes
  useEffect(() => {
    setIsLogin(initialMode !== 'register');
    setLoginMode('otp');
    setOtpStep('email');
    setConsentAccepted(false);
  }, [initialMode]);

  // Pre-fill from URL params — used by the AcceptInvitePage recovery
  // flow ("I already have a 6-digit code") which drops the user here
  // with `?email=...&mode=otp` so they don't have to retype it.
  useEffect(() => {
    const qEmail = search.get('email');
    const qMode = search.get('mode');
    if (qEmail) {
      setOtpEmail(qEmail);
      setFormData((prev) => ({ ...prev, email: qEmail }));
    }
    if (qMode === 'password') {
      setLoginMode('password');
    } else if (qMode === 'otp') {
      setLoginMode('otp');
    }
    // We deliberately run this only once on mount — Login is a top-level
    // landing route, so the params won't change during its lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OTP countdown timer
  useEffect(() => {
    if (otpTimer > 0) {
      const interval = setInterval(() => {
        setOtpTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [otpTimer]);

  // If user is logged in and has workspace selected, redirect
  if (user && currentWorkspace) {
    const isSuperAdmin = currentWorkspace.role === 'super_admin';
    if (isSuperAdmin) {
      return <Navigate to="/admin" />;
    }
    const role = currentWorkspace.role;
    if (role === 'auditor') {
      return <Navigate to="/auditor/dashboard" />;
    } else if (role === 'employee') {
      return <Navigate to="/employee/portal" />;
    }
    return <Navigate to="/dashboard" />;
  }

  // If user is logged in but no workspace selected (multiple workspaces)
  if (user && !currentWorkspace && workspaces && workspaces.length > 1) {
    return <Navigate to="/select-workspace" />;
  }

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
      setOtp(newOtp);
      otpInputRefs.current[Math.min(pastedData.length, 5)]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleSendOtp = async () => {
    if (!otpEmail) {
      toast.error('Please enter your email address');
      return;
    }
    if (!consentAccepted) {
      toast.error('Please accept the terms and conditions to continue');
      return;
    }
    setOtpLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, { email: otpEmail });
      toast.success('OTP sent to your email!');
      setOtpStep('verify');
      setOtpTimer(response.data.expires_in || 600);
      setOtp(['', '', '', '', '', '']);
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }
    setOtpLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        email: otpEmail,
        otp: otpCode
      });
      const data = response.data;
      localStorage.setItem('token', data.access_token);
      setUser(data.user);
      setWorkspaces(data.workspaces || []);
      setCurrentWorkspace(data.current_workspace);
      toast.success('Successfully logged in!');
      if (data.current_workspace?.role === 'super_admin') {
        localStorage.setItem('is_super_admin', 'true');
        navigate('/admin');
      } else if (data.workspaces && data.workspaces.length > 1 && !data.current_workspace) {
        navigate('/select-workspace');
      } else if (data.current_workspace) {
        const role = data.current_workspace.role;
        if (role === 'auditor') {
          navigate('/auditor/dashboard');
        } else if (role === 'employee') {
          navigate('/employee/portal');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, { email: otpEmail });
      toast.success('New OTP sent!');
      setOtpTimer(response.data.expires_in || 600);
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consentAccepted) {
      toast.error('Please accept the terms and conditions to continue');
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        toast.success('Successfully logged in!');
        if (result.current_workspace?.role === 'super_admin') {
          localStorage.setItem('is_super_admin', 'true');
          navigate('/admin');
          return;
        }
        if (result.workspaces && result.workspaces.length > 1 && !result.current_workspace) {
          navigate('/select-workspace');
        } else if (result.current_workspace) {
          const role = result.current_workspace.role;
          if (role === 'owner' || role === 'admin') {
            navigate('/dashboard');
          } else if (role === 'employee') {
            navigate('/employee/portal');
          } else if (role === 'auditor') {
            navigate('/auditor/dashboard');
          } else {
            navigate('/dashboard');
          }
        }
      } else {
        if (!formData.workspaceName.trim()) {
          toast.error('Please enter a workspace name');
          setLoading(false);
          return;
        }
        const result = await register(
          formData.email,
          formData.password,
          formData.fullName,
          formData.workspaceName
        );
        if (result.workspace_name && result.approval_status === 'approved') {
          toast.success(result.message || `Workspace '${result.workspace_name}' created! Please login to access it.`);
          setIsLogin(true);
          setFormData({ ...formData, password: '' });
        } else {
          toast.success(result.message || 'Registration successful! Awaiting approval.');
          navigate('/pending-approval', { state: { email: formData.email } });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setForgotPasswordLoading(true);
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowForgotPassword(false);
        setForgotPasswordEmail('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Branding panel content (left side)
  const heroTint = isLogin ? '#D4E0DA' : '#EBD8C8'; // sage for sign-in, peach for register
  const overline = isLogin ? 'Welcome back' : 'Get started';
  const headline = isLogin ? 'Sign in to' : 'Create your';
  const headlineItalic = isLogin ? 'continue.' : 'workspace.';
  const heroBlurb = isLogin
    ? 'Pick up where you left off — your audit progress, evidence, and tasks are exactly where you left them.'
    : 'Start your compliance journey today. Set up a workspace in seconds and let AI prepare you for SOC 2, GDPR, ISO 27001, and more.';

  const trustPoints = [
    { icon: Shield,      tint: '#D4E0DA', label: 'AI-powered compliance and certification' },
    { icon: Zap,         tint: '#EBD8C8', label: '80% of evidence collected automatically' },
    { icon: CheckCircle, tint: '#D9CFD9', label: 'Trusted independent auditor coordination' },
    { icon: Sparkles,    tint: '#D4E0DA', label: 'Continuous compliance monitoring' },
  ];

  // Card title/description for the form pane
  const cardTitle = isLogin ? 'Sign in' : 'Create account';
  const cardDescription = isLogin
    ? loginMode === 'otp'
      ? "We'll email you a one-time login code."
      : 'Use your email and password to sign in.'
    : 'Set up your workspace and start your 30-day free trial.';

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] font-body">
      <Header />

      <main className="pt-24 md:pt-28 pb-24 md:pb-32 px-6" data-testid={isLogin ? 'login-main' : 'register-main'}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-stretch">

          {/* ===================== LEFT: BRANDING PANEL ===================== */}
          <section
            className="nv-auth-panel rounded-2xl border border-black/10 dark:border-white/10 p-10 md:p-12 flex flex-col justify-between"
            style={{ backgroundColor: heroTint + '8C' }}
            data-testid="auth-brand-panel"
          >
            <div>
              <p className="font-label text-[11px] uppercase tracking-[0.3em] text-black/55 dark:text-white/55 mb-8">
                {overline}
              </p>
              <h1 className="font-display font-semibold leading-[1.25] tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-6">
                {headline}
                <br />
                <span className="italic text-black/85 dark:text-white/90">{headlineItalic}</span>
              </h1>
              <p className="font-body text-base md:text-lg text-black/70 dark:text-white/70 leading-relaxed max-w-md mb-12">
                {heroBlurb}
              </p>
            </div>

            <ul className="space-y-5">
              {trustPoints.map(({ icon: Icon, tint, label }) => (
                <li key={label} className="flex items-center gap-4">
                  <span
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 nv-tint"
                    style={{ backgroundColor: tint + 'CC' }}
                  >
                    <Icon className="w-4 h-4 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                  </span>
                  <span className="font-body text-sm md:text-base text-black/80 dark:text-white/85 leading-snug">{label}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* ===================== RIGHT: FORM PANEL ===================== */}
          <section
            className="bg-[#FAFAFA] dark:bg-[#050505] rounded-2xl border border-black/10 dark:border-white/10 p-8 md:p-10 flex flex-col"
            data-testid="auth-form-panel"
          >
            <div className="mb-8">
              <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight leading-tight mb-2">
                {cardTitle}
              </h2>
              <p className="font-body text-sm text-black/60 dark:text-white/60">{cardDescription}</p>
            </div>

            {isLogin ? (
              <>
                {/* OTP Login Mode */}
                {loginMode === 'otp' ? (
                  <div className="space-y-5">
                    {otpStep === 'email' ? (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="otpEmail" className="font-label text-[11px] uppercase tracking-[0.2em] text-black/55 dark:text-white/55">
                            Email Address
                          </Label>
                          <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 dark:text-white/45" strokeWidth={1.5} />
                            <Input
                              id="otpEmail"
                              type="email"
                              value={otpEmail}
                              onChange={(e) => setOtpEmail(e.target.value)}
                              className={`${inputCx} pl-11`}
                              onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                              data-testid="otp-email-input"
                            />
                          </div>
                        </div>

                        <div className="flex items-start gap-3 pt-1">
                          <input
                            type="checkbox"
                            id="consent-otp"
                            checked={consentAccepted}
                            onChange={(e) => setConsentAccepted(e.target.checked)}
                            className="mt-1 rounded-sm border-black/30 dark:border-white/30 text-[#0A0A0A] dark:text-[#F5F5F5] focus:ring-0 focus:ring-offset-0"
                            data-testid="consent-otp"
                          />
                          <label htmlFor="consent-otp" className="text-xs text-black/65 dark:text-white/65 leading-relaxed">
                            I agree to the{' '}
                            <a href="/dpa" className="text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-2 hover:no-underline">DPA</a>,{' '}
                            <a href="/privacy-policy" className="text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-2 hover:no-underline">Privacy Policy</a>
                            {', and '}
                            <a href="/terms-of-service" className="text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-2 hover:no-underline">Terms of Service</a>.
                          </label>
                        </div>

                        <Button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={otpLoading || !consentAccepted}
                          data-testid="send-otp-btn"
                          className="w-full h-12 rounded-full font-label uppercase tracking-[0.2em] text-xs bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] hover:bg-[#EBD8C8] dark:hover:bg-[#2E1B10] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] transition-all duration-500 disabled:opacity-50"
                        >
                          {otpLoading ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                              Sending code…
                            </>
                          ) : (
                            <>
                              <Mail className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                              Continue with email code
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="text-center mb-2">
                          <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-2">
                            Verify Email
                          </p>
                          <p className="font-body text-sm text-black/60 dark:text-white/60 mb-1">Enter the 6-digit code sent to</p>
                          <p className="font-display text-lg leading-tight">{otpEmail}</p>
                        </div>

                        <div className="flex justify-center gap-2 my-2" data-testid="otp-inputs">
                          {otp.map((digit, index) => (
                            <Input
                              key={index}
                              ref={(el) => (otpInputRefs.current[index] = el)}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleOtpChange(index, e.target.value)}
                              onKeyDown={(e) => handleOtpKeyDown(index, e)}
                              onPaste={index === 0 ? handleOtpPaste : undefined}
                              className="w-12 h-14 text-center font-display text-2xl bg-[#FAFAFA] dark:bg-[#050505] border-black/15 dark:border-white/15 focus-visible:border-[#0A0A0A] dark:focus-visible:border-white/30 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-xl"
                              data-testid={`otp-digit-${index}`}
                            />
                          ))}
                        </div>

                        {otpTimer > 0 && (
                          <p className="text-center text-xs text-black/55 dark:text-white/55">
                            Code expires in <span className="font-display text-[#0A0A0A] dark:text-[#F5F5F5]">{formatTime(otpTimer)}</span>
                          </p>
                        )}

                        <Button
                          type="button"
                          onClick={handleVerifyOtp}
                          disabled={otpLoading || otp.join('').length !== 6}
                          data-testid="verify-otp-btn"
                          className="w-full h-12 rounded-full font-label uppercase tracking-[0.2em] text-xs bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] hover:bg-[#EBD8C8] dark:hover:bg-[#2E1B10] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] transition-all duration-500 disabled:opacity-50"
                        >
                          {otpLoading ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                              Verifying…
                            </>
                          ) : (
                            <>
                              <KeyRound className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                              Verify &amp; Sign In
                            </>
                          )}
                        </Button>

                        <div className="flex items-center justify-between text-xs">
                          <button
                            type="button"
                            onClick={() => {
                              setOtpStep('email');
                              setOtp(['', '', '', '', '', '']);
                            }}
                            className="text-black/65 dark:text-white/65 hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] flex items-center gap-1.5"
                            data-testid="otp-change-email-btn"
                          >
                            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                            Change email
                          </button>
                          <button
                            type="button"
                            onClick={handleResendOtp}
                            disabled={otpLoading || otpTimer > 540}
                            className="text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-2 hover:no-underline disabled:text-black/35 dark:disabled:text-white/40 disabled:no-underline disabled:cursor-not-allowed"
                            data-testid="otp-resend-btn"
                          >
                            Resend code
                          </button>
                        </div>
                      </>
                    )}

                    {/* Divider */}
                    <div className="relative my-7" aria-hidden="true">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-black/10 dark:border-white/10" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-[#FAFAFA] dark:bg-[#050505] px-3 font-label text-[10px] uppercase tracking-[0.25em] text-black/45 dark:text-white/50">
                          Or
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setLoginMode('password');
                        setOtpStep('email');
                        setOtp(['', '', '', '', '', '']);
                      }}
                      data-testid="switch-to-password-btn"
                      className="w-full h-12 rounded-full font-label uppercase tracking-[0.2em] text-xs border border-black/15 dark:border-white/15 text-[#0A0A0A] dark:text-[#F5F5F5] hover:border-[#0A0A0A] dark:hover:border-white/30 hover:bg-[#D9CFD9]/40 dark:hover:bg-[#1E1724]/40 transition-all duration-500 inline-flex items-center justify-center gap-2"
                    >
                      <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Sign in with password
                    </button>
                  </div>
                ) : (
                  /* Password Login Mode */
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-label text-[11px] uppercase tracking-[0.2em] text-black/55 dark:text-white/55">
                        Email Address
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 dark:text-white/45" strokeWidth={1.5} />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className={`${inputCx} pl-11`}
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="font-label text-[11px] uppercase tracking-[0.2em] text-black/55 dark:text-white/55">
                          Password
                        </Label>
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          data-testid="forgot-password-btn"
                          className="text-[11px] text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-2 hover:no-underline"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 dark:text-white/45" strokeWidth={1.5} />
                        <Input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                          className={`${inputCx} pl-11 pr-11`}
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-black/45 dark:text-white/50 hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5]"
                          data-testid="login-toggle-password"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 pt-1">
                      <input
                        type="checkbox"
                        id="consent-password"
                        checked={consentAccepted}
                        onChange={(e) => setConsentAccepted(e.target.checked)}
                        className="mt-1 rounded-sm border-black/30 dark:border-white/30 text-[#0A0A0A] dark:text-[#F5F5F5] focus:ring-0 focus:ring-offset-0"
                        data-testid="consent-password"
                      />
                      <label htmlFor="consent-password" className="text-xs text-black/65 dark:text-white/65 leading-relaxed">
                        I agree to the{' '}
                        <a href="/dpa" className="text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-2 hover:no-underline">DPA</a>,{' '}
                        <a href="/privacy-policy" className="text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-2 hover:no-underline">Privacy Policy</a>
                        {', and '}
                        <a href="/terms-of-service" className="text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-2 hover:no-underline">Terms of Service</a>.
                      </label>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || !consentAccepted}
                      data-testid="login-submit-btn"
                      className="w-full h-12 rounded-full font-label uppercase tracking-[0.2em] text-xs bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] hover:bg-[#EBD8C8] dark:hover:bg-[#2E1B10] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] transition-all duration-500 disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                          Signing in…
                        </>
                      ) : (
                        <>
                          <Lock className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                          Sign in with password
                        </>
                      )}
                    </Button>

                    <div className="relative my-2" aria-hidden="true">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-black/10 dark:border-white/10" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-[#FAFAFA] dark:bg-[#050505] px-3 font-label text-[10px] uppercase tracking-[0.25em] text-black/45 dark:text-white/50">
                          Or
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setLoginMode('otp')}
                      data-testid="switch-to-otp-btn"
                      className="w-full h-12 rounded-full font-label uppercase tracking-[0.2em] text-xs border border-black/15 dark:border-white/15 text-[#0A0A0A] dark:text-[#F5F5F5] hover:border-[#0A0A0A] dark:hover:border-white/30 hover:bg-[#D4E0DA]/40 dark:hover:bg-[#101F18]/40 transition-all duration-500 inline-flex items-center justify-center gap-2"
                    >
                      <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Sign in with email code
                    </button>
                  </form>
                )}
              </>
            ) : (
              /* Registration Form */
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="font-label text-[11px] uppercase tracking-[0.2em] text-black/55 dark:text-white/55">
                    Full Name
                  </Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className={inputCx}
                    data-testid="register-fullname-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="font-label text-[11px] uppercase tracking-[0.2em] text-black/55 dark:text-white/55">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={inputCx}
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="font-label text-[11px] uppercase tracking-[0.2em] text-black/55 dark:text-white/55">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className={`${inputCx} pr-11`}
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-black/45 dark:text-white/50 hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5]"
                      data-testid="register-toggle-password"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="workspaceName" className="font-label text-[11px] uppercase tracking-[0.2em] text-black/55 dark:text-white/55">
                    Organization / Workspace
                  </Label>
                  <Input
                    id="workspaceName"
                    name="workspaceName"
                    type="text"
                    value={formData.workspaceName}
                    onChange={handleInputChange}
                    required
                    className={inputCx}
                    data-testid="register-workspace-input"
                  />
                </div>

                <div className="flex items-start gap-3 pt-1">
                  <input
                    type="checkbox"
                    id="consent-register"
                    checked={consentAccepted}
                    onChange={(e) => setConsentAccepted(e.target.checked)}
                    className="mt-1 rounded-sm border-black/30 dark:border-white/30 text-[#0A0A0A] dark:text-[#F5F5F5] focus:ring-0 focus:ring-offset-0"
                    data-testid="consent-register"
                  />
                  <label htmlFor="consent-register" className="text-xs text-black/65 dark:text-white/65 leading-relaxed">
                    I agree to the{' '}
                    <a href="/dpa" className="text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-2 hover:no-underline">DPA</a>,{' '}
                    <a href="/privacy-policy" className="text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-2 hover:no-underline">Privacy Policy</a>
                    {' and '}
                    <a href="/terms-of-service" className="text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-2 hover:no-underline">Terms of Service</a>.
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !consentAccepted}
                  data-testid="register-submit-btn"
                  className="w-full h-12 rounded-full font-label uppercase tracking-[0.2em] text-xs bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] hover:bg-[#D4E0DA] dark:hover:bg-[#101F18] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] transition-all duration-500 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                      Creating account…
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>
            )}

            {/* Toggle Login/Register */}
            <div className="text-center pt-8 mt-auto border-t border-black/10 dark:border-white/10 mt-8">
              <p className="text-sm text-black/65 dark:text-white/65">
                {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
                <button
                  type="button"
                  onClick={() => {
                    if (isLogin) {
                      navigate('/register');
                    } else {
                      navigate('/login');
                    }
                  }}
                  data-testid="toggle-auth-mode-btn"
                  className="font-label uppercase tracking-[0.2em] text-[11px] text-[#0A0A0A] dark:text-[#F5F5F5] underline underline-offset-4 hover:no-underline"
                >
                  {isLogin ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            </div>
          </section>
        </div>
      </main>

      <Footer />

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="bg-[#FAFAFA] dark:bg-[#050505] border-black/15 dark:border-white/15 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display font-semibold text-2xl tracking-tight">Reset password</DialogTitle>
            <DialogDescription className="font-body text-sm text-black/60 dark:text-white/60">
              Enter your email and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="forgot-email" className="font-label text-[11px] uppercase tracking-[0.2em] text-black/55 dark:text-white/55">
              Email Address
            </Label>
            <Input
              id="forgot-email"
              type="email"
              placeholder="you@company.com"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              className={inputCx}
              data-testid="forgot-email-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowForgotPassword(false)}
              data-testid="forgot-cancel-btn"
              className="rounded-full h-11 px-6 border-black/15 dark:border-white/15 text-[#0A0A0A] dark:text-[#F5F5F5] hover:bg-[#D9CFD9]/40 dark:hover:bg-[#1E1724]/40 hover:border-[#0A0A0A] dark:hover:border-white/30 font-label uppercase tracking-[0.2em] text-[11px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleForgotPassword}
              disabled={forgotPasswordLoading}
              data-testid="forgot-send-btn"
              className="rounded-full h-11 px-6 bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] hover:bg-[#EBD8C8] dark:hover:bg-[#2E1B10] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] font-label uppercase tracking-[0.2em] text-[11px] disabled:opacity-50"
            >
              {forgotPasswordLoading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
